import {MathUtils} from 'pixi-spine';
import {Device} from '@gongxigames/framework/core/services/Device';

type ScrollOptions = {
    searchEnabled?: boolean;
    contentClass?: string;
};

const DEFAULT_SCROLL_OPTIONS: ScrollOptions = {
    contentClass: '.content',
    searchEnabled: false
};

export class Scroll {
    public static readonly WHEEL_STEP = {
        default: 40,
        safari: 25
    };

    protected _options?: ScrollOptions;
    protected _parent: HTMLElement;
    protected _scrollContainer!: HTMLElement;
    protected _scroll!: HTMLElement;
    protected _content: HTMLElement;
    protected _contentScrollableHeight!: number;
    protected _scrollableBarHeight!: number;
    protected _currentHeight = 0;
    protected _device: Device;
    protected _moveAtHandler: (...args: any[]) => void = this.moveAt.bind(this, 0);
    protected _mousedownHandler: (e: any) => void = this.onMouseDown.bind(this);
    protected _mouseupHandler: (e: any) => void = this.onMouseUp.bind(this);
    protected _dragstartHandler: (e: any) => boolean = this.onDragStart.bind(this);
    protected _wheelHandler: (e: any) => void = this.onWheel.bind(this);
    protected _scrollHandler: (e: MouseEvent) => void = this.onScroll.bind(this);

    constructor(parent: HTMLElement, device: Device, options: ScrollOptions = DEFAULT_SCROLL_OPTIONS) {
        this._parent = parent;
        this._content = this._parent.querySelector(options.contentClass ?? DEFAULT_SCROLL_OPTIONS.contentClass!) as HTMLElement;
        this._device = device;
        this._options = options;

        this.createScrollBar();
        this.getHeights();
        this.initEvents();
    }

    public removeListeners(): void {
        this._parent.removeEventListener('wheel', this._wheelHandler);
        this._scroll.removeEventListener('mousedown', this._mousedownHandler);
        this._scroll.removeEventListener('ondragstart', this._dragstartHandler);
        this._scrollContainer.removeEventListener('click', this._scrollHandler);

        document.removeEventListener('mousemove', this._moveAtHandler);
        document.removeEventListener('mouseup', this._mouseupHandler);
    }

    protected createScrollBar(): void {
        this._scrollContainer = document.createElement('div');
        this._scrollContainer.id = 'scrollBarContainer';
        this._parent.appendChild(this._scrollContainer);

        if (this._options?.searchEnabled) {
            this._scrollContainer.classList.add('search-enabled');
        }

        this._scroll = document.createElement('div');
        this._scroll.id = 'scroll';
        this._scrollContainer.appendChild(this._scroll);
    }

    protected getHeights(): void {
        const contentRect = this._content.getBoundingClientRect();
        const parentRect = this._parent.getBoundingClientRect();
        const title = this._parent.querySelector('.title')!;
        const titleHeight = title?.getBoundingClientRect().height ?? 0;

        this._contentScrollableHeight = contentRect.height - parentRect.height + titleHeight;
        this._scrollableBarHeight = this._scrollContainer.getBoundingClientRect().height - this._scroll.getBoundingClientRect().height;
    }

    protected initEvents(): void {
        this.initScrollWheel();
        this.initDragNDropForScroll();
        this.initScrollByClick();
    }

    protected initScrollWheel(): void {
        this._parent.addEventListener('wheel', this._wheelHandler);
    }

    protected onWheel(e: WheelEvent): void {
        e.preventDefault();

        const step = this._device.chrome ? e.deltaY :
            (e.deltaY > 0 ? 1 : -1) * (this._device.iOS ? Scroll.WHEEL_STEP.safari : Scroll.WHEEL_STEP.default);

        this._currentHeight += step / this._contentScrollableHeight;
        this._currentHeight = MathUtils.clamp(this._currentHeight, 0, 1);

        this.updatePositions();
    }

    protected initDragNDropForScroll(): void {
        this._scroll.addEventListener('mousedown', this._mousedownHandler);
        this._scroll.addEventListener('ondragstart', this._dragstartHandler);
    }

    protected onDragStart(e: DragEvent): boolean {
        return false;
    }

    protected onMouseDown(e: MouseEvent): boolean | undefined {
        // draggable by left button only
        if (e.which !== 1) {
            return;
        }

        const scrollRect = this._scroll.getBoundingClientRect();
        const shiftY = e.pageY - scrollRect.top;

        this._moveAtHandler = this.moveAt.bind(this, shiftY);

        document.addEventListener('mousemove', this._moveAtHandler);
        document.addEventListener('mouseup', this._mouseupHandler);
    }

    protected onMouseUp(e: MouseEvent): void {
        document.removeEventListener('mousemove', this._moveAtHandler);
    }

    protected initScrollByClick(): void {
        this._scrollContainer.addEventListener('click', this._scrollHandler);
    }

    protected onScroll(e: MouseEvent): void {
        if (e.target === this._scroll) {
            return;
        }

        const containerRect = this._scrollContainer.getBoundingClientRect();
        const scrollRect = this._scroll.getBoundingClientRect();
        let currentY = e.pageY - containerRect.top;

        // prevent to set scroll position below the scroll
        if (e.pageY > containerRect.bottom - scrollRect.height) {
            currentY = containerRect.height - scrollRect.height;
        }

        this._currentHeight = currentY / this._scrollableBarHeight;

        this.updatePositions();
    }

    public scrollTo(offsetY: number): void {
        this._currentHeight += offsetY / this._contentScrollableHeight;
        this._currentHeight = MathUtils.clamp(this._currentHeight, 0, 1);

        this.updatePositions();
    }

    protected moveAt(shiftY: number, e: any): void {
        const containerRect = this._scrollContainer.getBoundingClientRect();

        if (this.isPositionOutsideTheScrollBar(containerRect, shiftY, e.pageY)) {
            return;
        }

        const currentY = e.pageY - containerRect.top - shiftY;

        this._currentHeight = currentY / this._scrollableBarHeight;
        this.updatePositions();
    }

    protected isPositionOutsideTheScrollBar(containerRect: any, shiftY: number, y: number): boolean {
        return y < containerRect.top + shiftY || y > containerRect.bottom - this._scroll.clientHeight + shiftY;
    }

    protected updatePositions(): void {
        this._scroll.style.top = this._currentHeight * this._scrollableBarHeight + 'px';
        this._content.style.transform = `translateY(-${this._currentHeight * this._contentScrollableHeight}px)`;
    }

    public hide(): void {
        this._content.style.transform = 'none';
        this._scrollContainer.style.display = 'none';
    }

    public show(): void {
        this._scrollContainer.style.display = 'block';
    }

    public onResize(): void {
        this._currentHeight = 0;

        this.getHeights();
        this.updatePositions();
    }
}
